import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { SearchResults, Human, Event, Team, normalizeLocation } from '../models/data.models';
import { includesEveryNameToken, nameSearchTokens } from '../models/name-search';

@Injectable({
    providedIn: 'root'
})
export class SearchService {
    private supabase = inject(SupabaseService);

    async search(query: string): Promise<SearchResults> {
        // Commas and parentheses delimit PostgREST "or" filters, and % * are wildcards: a query
        // containing them would break the filter or widen it.
        const term = (query || '').replace(/[,()%*_\\"]/g, ' ').replace(/\s+/g, ' ').trim();
        if (term.length < 2) {
            return { events: [], teams: [], humans: [] };
        }

        const searchQuery = `%${term}%`;

        const humanTokens = nameSearchTokens(term);
        const anchorToken = humanTokens.reduce((longest, token) => token.length > longest.length ? token : longest, '');
        const humansQuery = this.supabase.client.from('humans')
            .select('id, name, age, gender, nationality, image_url, club:clubs(name)')
            .ilike('name', `%${anchorToken}%`);

        let [eventsFunc, teamsFunc, humansFunc] = await Promise.all([
            this.supabase.client
                .from('events')
                .select('*')
                .eq('is_published', true)
                .or(`name.ilike.${searchQuery},city.ilike.${searchQuery},department_code.ilike.${searchQuery}`)
                .limit(5),
            this.supabase.client.from('teams').select('*, human:humans(*), dog:dogs(*)').ilike('team_name', searchQuery).limit(5),
            humansQuery.limit(50)
        ]);

        // Keep global search functional during a staggered frontend/database
        // deployment. Once the geography migration exists, city and department
        // are searched too; older schemas gracefully fall back to event name.
        if (eventsFunc.error && /city|department_code/i.test(eventsFunc.error.message)) {
            eventsFunc = await this.supabase.client
                .from('events')
                .select('*')
                .eq('is_published', true)
                .ilike('name', searchQuery)
                .limit(5);
        }

        for (const response of [eventsFunc, teamsFunc, humansFunc]) {
            if (response.error) console.error('Search query failed:', response.error);
        }

        const humans: Human[] = (humansFunc.data || [])
          .filter(h => includesEveryNameToken(h.name, term))
          .slice(0, 5)
          .map(h => ({
            id: h.id,
            name: h.name,
            age: h.age,
            gender: h.gender,
            club: (Array.isArray(h.club) ? h.club[0]?.name : (h.club as { name?: string } | null)?.name) ?? undefined,
            nationality: h.nationality,
            imageUrl: h.image_url
          }));

        const events: Event[] = (eventsFunc.data || []).map(e => ({
            id: e.id,
            name: e.name,
            location: normalizeLocation(e.location),
            date: e.date,
            weather: e.weather,
            description: e.description,
            imageUrl: e.image_url,
            region: e.region,
            city: e.city || undefined,
            postalCode: e.postal_code || undefined,
            departmentCode: e.department_code || undefined,
            inseeCode: e.insee_code || undefined,
            latitude: typeof e.latitude === 'number' ? e.latitude : undefined,
            longitude: typeof e.longitude === 'number' ? e.longitude : undefined,
            address: e.address || undefined,
            geoPrecision: e.geo_precision || undefined,
            geoSource: e.geo_source || undefined,
            geoConfidence: e.geo_confidence || undefined,
            status: e.status,
            isPublished: e.is_published
        }));

        const teams: Team[] = (teamsFunc.data || []).filter(t => t.human && t.dog).map((t: any) => ({
            id: t.id,
            humanId: t.human_id,
            dogId: t.dog_id,
            teamName: t.team_name,
            human: {
                id: t.human.id,
                name: t.human.name,
                age: t.human.age,
                gender: t.human.gender,
                club: t.human.club,
                nationality: t.human.nationality,
                imageUrl: t.human.image_url
            },
            dog: {
                id: t.dog.id,
                ownerId: t.dog.owner_id,
                name: t.dog.name,
                age: t.dog.age,
                breed: t.dog.breed,
                gender: t.dog.gender,
                imageUrl: t.dog.image_url
            }
        }));

        return { events, teams, humans };
    }
}
